import React, { useState, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { FaVolumeUp, FaVolumeMute, FaPause, FaCopy } from 'react-icons/fa';
import PuzzleWorkspace, { PuzzlePanel } from '../components/puzzles/PuzzleWorkspace';
import { Button } from '../components/ui/button';
import PuzzleBoard from '../components/puzzles/PuzzleBoard';
import PuzzleActions from '../components/puzzles/PuzzleActions';
import { useNavigate, useLocation } from 'react-router-dom';
import { lichessPuzzleApi } from '../api';
import { useWoodpeckerAuth } from './Woodpecker';
import { ChessAnalysisPanel } from './ChessAnalysisPanel';
import { ChessPuzzle, useChessPuzzleContext } from '@react-chess-puzzle-fix/react-chess-puzzle';
import { ChessGame, useChessGameContext } from 'react-chess-game-fix';
import { Helmet } from "react-helmet";

// Default board size in px
const DEFAULT_BOARD_SIZE = window.innerHeight * 0.85;

// Add Timer component at the top level
const Timer = React.memo(React.forwardRef(function Timer({ isPaused, initialTime = 0 }, ref) {
    const [elapsed, setElapsed] = useState(initialTime);

    /* keep ticking locally */
    useEffect(() => {
      if (isPaused) return;
      const id = setInterval(() => setElapsed(t => t + 1), 1000);
      return () => clearInterval(id);
    }, [isPaused]);

    /* expose an imperative API to parents */
    useImperativeHandle(ref, () => ({
      /** returns current elapsed seconds */
      getElapsed() {
        return elapsed;
      },
    }), [elapsed]);

  // Format time from seconds to minutes and seconds
  const formatTime = (timeInSeconds) => {
    if (timeInSeconds === undefined || timeInSeconds === null) return '0m 0s';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}m ${seconds}s`;
  };

  return <span className="tabular-nums">{formatTime(elapsed)}</span>;
}));

const AutoRetryHandler = React.memo(function AutoRetryHandler({ onPuzzleSolved, onPuzzleFailed, setCurrentPuzzleAttempted, setWrongMove, puzzleCompleted }) {
  const { status, autoRetry, hint, currentMoveIndex } = useChessPuzzleContext();
  const { game, methods } = useChessGameContext();

  useEffect(() => {
      setWrongMove(null);
  }, [currentMoveIndex, setWrongMove]);

  useEffect(() => {
    if (!puzzleCompleted) {
      if (status === 'failed') {
        setCurrentPuzzleAttempted(true);
        onPuzzleFailed();
  
        setTimeout(() => {
          autoRetry();
          methods.undoMove();
          setWrongMove(game?.history()[game.history().length - 1]);
        }, 1000); // 1 second delay for user to see incorrect move
      } else if (status === 'solved') {
        onPuzzleSolved();
      } 
    }
  }, [status]);

  useEffect(() => {
    if (hint !== 'none') {
        setCurrentPuzzleAttempted(true);
    }
  }, [hint]);

  return null;
});

const PuzzleSection = React.memo(function PuzzleSection({ puzzles, puzzleIndex, setPuzzleIndex, handleDone, onPuzzleSolved, onPuzzleFailed, setCurrentPuzzleAttempted, setIsPaused, wrongMove, setWrongMove, puzzleCompleted, setPuzzleCompleted }) {
  const isLastPuzzle = puzzleIndex === puzzles.length - 1;

  return (
    <>
      <ChessGame.KeyboardControls />

      {/* Auto retry on fail */}
      <AutoRetryHandler onPuzzleSolved={onPuzzleSolved} onPuzzleFailed={onPuzzleFailed} setCurrentPuzzleAttempted={setCurrentPuzzleAttempted} setWrongMove={setWrongMove} puzzleCompleted={puzzleCompleted}/>

      <PuzzleActions
        showRestart={puzzleCompleted}
        showNext={puzzleCompleted && !isLastPuzzle}
        nextPuzzle={puzzles[(puzzleIndex + 1) % puzzles.length]}
        onRestart={() => setPuzzleCompleted(false)}
        onNext={() => {
          setPuzzleIndex((puzzleIndex + 1) % puzzles.length);
          setCurrentPuzzleAttempted(false);
          setPuzzleCompleted(false);
          setIsPaused(false);
        }}
        onDone={puzzleCompleted && isLastPuzzle ? handleDone : undefined}
      />
    </>
  );
});

function PuzzlePage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Destructure the state from location
  const {
    setId,
    setName,
    userEmail,
    userRating,
    numPuzzles: requestedNumPuzzles,
    startPuzzleIndex,
    initialTimeSpent,
    initialCycleNumber,
    initialSolvedCorrectly,
    sessionMode: requestedSessionMode = 'full',
  } = location.state || {};

  const timerRef = useRef(null);
  const wrongPuzzleIds = useRef(new Set());
  // Keep failures for this round so the automatic retry cannot clear a mistake.
  const failedPuzzleIds = useRef(new Set());
  const saveQueue = useRef(Promise.resolve());
  const [puzzles, setPuzzles] = useState([]);
  const [puzzleIndex, setPuzzleIndex] = useState(startPuzzleIndex || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(true);
  const [sessionMode, setSessionMode] = useState(requestedSessionMode);
  const [timeAtStart, setTimeAtStart] = useState(initialTimeSpent || 0);
  const [saveError, setSaveError] = useState(null);
  const [cycleNumber, setCycleNumber] = useState(initialCycleNumber || 1);
  const [soundsEnabled, setSoundsEnabled] = useState(false);
  const [solvedCorrectly, setSolvedCorrectly] = useState(initialSolvedCorrectly || 0);
  const [currentPuzzleAttempted, setCurrentPuzzleAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showFenTooltip, setShowFenTooltip] = useState(false);
  const { idToken, authProvider, isPremium, setPuzzleData } = useWoodpeckerAuth();
  const [boardSize, setBoardSize] = useState(DEFAULT_BOARD_SIZE);
  const [wrongMove, setWrongMove] = useState(null);
  const [puzzleCompleted, setPuzzleCompleted] = useState(false);

  // Add validation effect
  useEffect(() => {
    if (!userEmail || !userRating || !requestedNumPuzzles) {
      console.error('Missing required props:', {
        userEmail,
        userRating,
        requestedNumPuzzles
      });
      navigate('/woodpecker/briefing');
    }
  }, [userEmail, userRating, requestedNumPuzzles, navigate]);

  const fetchPuzzles = async (rating, numPuzzles, email) => {
    if (rating === undefined || numPuzzles === undefined || email === undefined) {
      setError('User rating, number of puzzles, or email not provided.');
      console.error('Error fetching puzzles: User rating, number of puzzles, or email not provided.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await lichessPuzzleApi.fetchPuzzles(idToken, authProvider, email, rating, numPuzzles, {
        setId,
        setName,
        startPuzzleIndex,
        initialTimeSpent,
        initialCycleNumber,
        sessionMode: requestedSessionMode,
      });

      if (data && data.puzzles && Array.isArray(data.puzzles)) {
        const progress = data.currentProgress || {};
        const mode = progress.sessionMode || 'full';
        const selectedIds = new Set(progress.puzzleIds || []);
        const roundPuzzles = mode === 'mistakes'
          ? data.puzzles.filter(puzzle => selectedIds.has(puzzle.puzzleId))
          : data.puzzles;
        if (!roundPuzzles.length) throw new Error('No puzzles available for this cycle. Return to your training set.');
        const puzzlesWithFirstMove = roundPuzzles.map(puzzle => ({
          ...puzzle,
          makeFirstMove: true
        }));
        setPuzzles(puzzlesWithFirstMove);
        setPuzzleIndex(progress.puzzlesCompleted || 0);
        setCycleNumber(data.cycleNumber || 1);
        setSessionMode(mode);
        setTimeAtStart(progress.currentTime || 0);
        setSolvedCorrectly(progress.solvedCorrectly || 0);
        wrongPuzzleIds.current = new Set(data.wrongPuzzleIds || []);
        setCurrentPuzzleAttempted(progress.currentPuzzleAttempted === true);
        failedPuzzleIds.current = new Set(progress.currentPuzzleFailed && roundPuzzles[progress.puzzlesCompleted || 0]
          ? [roundPuzzles[progress.puzzlesCompleted || 0].puzzleId] : []);
        const { puzzles: storedPuzzles, ...setSummary } = data;
        setPuzzleData(previous => {
          const sets = Array.isArray(previous) ? previous : [];
          return sets.some(set => set.setId === setId)
            ? sets.map(set => set.setId === setId ? setSummary : set)
            : [...sets, setSummary];
        });
        setIsPaused(false);
      } else {
        setError('Invalid data format from puzzle API.');
        console.error('Invalid data format from puzzle API:', data);
        setPuzzles([]);
      }
    } catch (error) {
      if (error && error.status === 403) {
        console.warn('Auth expired or forbidden. Redirecting to onboarding.');
        navigate('/woodpecker');
        return;
      }
      setError(error.message);
      console.error('Error fetching puzzles:', error);
      setPuzzles([]);
      setPuzzleIndex(startPuzzleIndex || 0);
    //   saveElapsed(initialTimeSpent || 0);
      setCycleNumber(initialCycleNumber || 1);
    } finally {
      setLoading(false);
    }
  };

  // Fetch puzzles automatically when the component mounts or relevant props change
  useEffect(() => {
    if (!loading && userRating !== undefined && requestedNumPuzzles !== undefined && userEmail !== undefined) {
      fetchPuzzles(userRating, requestedNumPuzzles, userEmail);
    }
  }, [userRating, requestedNumPuzzles, userEmail]);

  // Effect to update local state based on initial props ONLY when they first arrive
  useEffect(() => {
      setPuzzleIndex(startPuzzleIndex || 0);
    //   saveElapsed(initialTimeSpent || 0);
      setCycleNumber(initialCycleNumber || 1); // Update cycle number from initial prop
  }, [startPuzzleIndex, initialTimeSpent, initialCycleNumber]); // Only run when initial props change

  useEffect(() => {
    if (puzzles.length && puzzleIndex % 10 === 0 && puzzleIndex !== 0 && puzzleIndex < puzzles.length - 1) {
      const seconds = timerRef.current?.getElapsed() ?? 0;
      handleSave(puzzleIndex, seconds, false, true);
    }
  }, [puzzleIndex]);

  // Get the current puzzle based on the puzzleIndex
  const currentPuzzle = puzzles[puzzleIndex];

  const handlePause = async () => {
    if (isSaving) return;
    setIsPaused(true); // Pause the timer, this triggers saveElapsed()
    setIsSaving(true);
    const seconds = timerRef.current?.getElapsed() ?? 0;   // 1️⃣ read before or after pausing

    console.log('Attempting to save: Current Time -', seconds, ', Puzzle Index -', puzzleIndex, ', User Email -', userEmail, ', Cycle Number -', cycleNumber, ', Solved Correctly -', solvedCorrectly);

    // Resume unfinished puzzles, preserving whether a wrong move or hint was used.
    const finalPuzzleIndex = puzzleCompleted ? puzzleIndex + 1 : puzzleIndex;

    const cycleDone = finalPuzzleIndex === puzzles.length;
    const response = await handleSave(finalPuzzleIndex, seconds, cycleDone, !cycleDone);
    if (!response) return;

    // Navigate to briefing page with accumulated progress from the API response
    navigate('/woodpecker/briefing', {
      state: {
        currentTime: seconds,
        setId,
        setName,
        ...response,
        puzzleIndex: finalPuzzleIndex,
        cycleNumber: response.cycleNumber,
        cycles: response.cycles,
        solvedCorrectly: solvedCorrectly,
        userEmail,
        userRating,
        puzzleQuantity: requestedNumPuzzles,
        midCycle: !cycleDone,
      }
    });
  };

  const handleSave = async (puzzleIndextoSave, seconds, cycleDone, midCycle) => {
    console.log('Saving: Puzzle Index -', puzzleIndextoSave, ', Seconds -', seconds, ', Cycle Done -', cycleDone, ', Mid Cycle -', midCycle);
    const payload = {
      setId, currentTime: seconds, solvedCorrectly,
      puzzlesCompleted: puzzleIndextoSave, cycleDone, midCycle,
      cycleNumber,
      wrongPuzzleIds: [...wrongPuzzleIds.current],
      currentPuzzleAttempted: puzzleIndextoSave === puzzleIndex && currentPuzzleAttempted,
      currentPuzzleFailed: puzzleIndextoSave === puzzleIndex && failedPuzzleIds.current.has(currentPuzzle?.puzzleId),
    };
    // Keep autosaves ordered before Pause/Done so an older cursor cannot overwrite them.
    const pending = saveQueue.current.then(() => lichessPuzzleApi.pauseProgress(idToken, authProvider, userEmail, payload));
    saveQueue.current = pending.catch(() => {});
    try {
      const response = await pending;
      setSaveError(null);
      setPuzzleData(previous => Array.isArray(previous)
        ? previous.map(set => set.setId === setId ? { ...set, ...response } : set)
        : previous);
      return response
    } catch (error) {
      console.error('Error saving game state:', error);
      setSaveError(`Progress was not saved: ${error.message} Try Pause & Save again.`);
      setIsSaving(false);
      setIsPaused(puzzleCompleted);
      return null;
    }
  }

  const handleDone = async () => {
    if (isSaving) return;
    setIsPaused(true); // This triggers saveElapsed()
    setIsSaving(true);
    const seconds = timerRef.current?.getElapsed() ?? 0;   // 1️⃣ read before or after pausing
    console.log('Puzzle Set Complete: Current Time -', seconds, ', Puzzle Index -', puzzleIndex, ', User Email -', userEmail, ', Cycle Number -', cycleNumber, ', Solved Correctly -', solvedCorrectly);

    const finalPuzzleIndex = puzzles.length;

    const response = await handleSave(finalPuzzleIndex, seconds, true, false);
    if (!response) return;
    // Navigate to briefing page with the response data
    navigate('/woodpecker/briefing', {
      state: {
        currentTime: response.currentProgress.currentTime,
        setId,
        setName,
        ...response,
        puzzleIndex: finalPuzzleIndex, // Not shown in Briefing for done. Could probably change to response.currentProgress.puzzlesCompleted
        cycleNumber: response.cycleNumber,
        cycles: response.cycles,
        solvedCorrectly: solvedCorrectly, // Not shown in Briefing for done
        userEmail,
        userRating,
        puzzleQuantity: requestedNumPuzzles,
        midCycle: false
      }
    });
  };

  const handlePuzzleSolved = () => {
    setIsPaused(true); // Stop the timer when puzzle is solved
    setWrongMove(null);
    setPuzzleCompleted(true);
    if (!failedPuzzleIds.current.has(currentPuzzle.puzzleId)) {
      wrongPuzzleIds.current.delete(currentPuzzle.puzzleId);
    }
    if (!currentPuzzleAttempted) {
      setCurrentPuzzleAttempted(true);
      setSolvedCorrectly(prev => prev + 1);
    }
  };

  const handlePuzzleFailed = () => {
    failedPuzzleIds.current.add(currentPuzzle.puzzleId);
    wrongPuzzleIds.current.add(currentPuzzle.puzzleId);
  };

  const handleCopyFEN = async () => {
    if (currentPuzzle && currentPuzzle.fen) {
      try {
        await navigator.clipboard.writeText(currentPuzzle.fen);
        setShowFenTooltip(true);
        setTimeout(() => setShowFenTooltip(false), 1500); // Hide after 1.5 seconds
        console.log('FEN copied to clipboard:', currentPuzzle.fen);
      } catch (err) {
        console.error('Failed to copy FEN:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = currentPuzzle.fen;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShowFenTooltip(true);
        setTimeout(() => setShowFenTooltip(false), 1500); // Hide after 1.5 seconds
      }
    }
  };

//   const timer = Timer(isPaused, initialTimeSpent, saveElapsed);
  const timer = useMemo(
    () => (
      <Timer
        isPaused={isPaused}
        initialTime={timeAtStart}
        ref={timerRef}
      />
    ),
    [isPaused, timeAtStart]
  );

  if (currentPuzzle) {
    return (
      <ChessPuzzle.Root puzzle={currentPuzzle}>
        <Helmet>
          <title>Woodpecker Puzzle Session | Puzzlify</title>
          <meta name="robots" content="noindex,follow" />
          <link rel="canonical" href="https://puzzlify.io/woodpecker/puzzle" />
        </Helmet>
        <PuzzleWorkspace
          title="Woodpecker training"
          subtitle={<>
            Puzzle {puzzleIndex + 1} of {puzzles.length} · Cycle {cycleNumber}
            {sessionMode === 'mistakes' && <span className="ml-2 font-semibold text-sky-700">Mistakes only</span>}
            <span className="mt-1 block" aria-label="Time spent">{timer}</span>
          </>}
          onBack={handlePause}
          backLabel="Pause and save"
          backIcon={FaPause}
          backDisabled={isSaving || loading || Boolean(error)}
          toolbar={<>
            <Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0"
              onClick={() => setSoundsEnabled(!soundsEnabled)} aria-pressed={soundsEnabled}
              aria-label={soundsEnabled ? 'Mute sounds' : 'Enable sounds'} title={soundsEnabled ? 'Mute sounds' : 'Enable sounds'}>
              {soundsEnabled ? <FaVolumeUp aria-hidden="true" /> : <FaVolumeMute aria-hidden="true" />}
            </Button>
            <Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0"
              onClick={handleCopyFEN} aria-label="Copy original puzzle FEN" title="Copy original puzzle FEN">
              <FaCopy aria-hidden="true" />
            </Button>
          </>}
          sidebar={<>
            <ChessAnalysisPanel wrongMove={wrongMove} isPremium={isPremium} puzzleIndex={puzzleIndex} />
            <div className="hidden sm:block">
              <PuzzlePanel title="Board size">
                <label htmlFor="board-size-slider" className="sr-only">Board size</label>
                <input id="board-size-slider" type="range" min="320" max="700" step="10" value={boardSize}
                  onChange={e => setBoardSize(Number(e.target.value))} className="w-full accent-sky-700" />
              </PuzzlePanel>
            </div>
          </>}
        >
          {soundsEnabled && <ChessGame.Sounds />}
          <PuzzleBoard boardSize={boardSize} />
          <PuzzleSection
            puzzles={puzzles}
            puzzleIndex={puzzleIndex}
            setPuzzleIndex={setPuzzleIndex}
            handleDone={handleDone}
            onPuzzleSolved={handlePuzzleSolved}
            onPuzzleFailed={handlePuzzleFailed}
            setCurrentPuzzleAttempted={setCurrentPuzzleAttempted}
            setIsPaused={setIsPaused}
            wrongMove={wrongMove}
            setWrongMove={setWrongMove}
            puzzleCompleted={puzzleCompleted}
            setPuzzleCompleted={setPuzzleCompleted}
          />
          {showFenTooltip && <p role="status" className="px-4 text-center text-sm text-sky-700">FEN copied to clipboard.</p>}
          {saveError && <p role="alert" className="mx-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{saveError}</p>}
        </PuzzleWorkspace>
        {isSaving && (
          <div role="status" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <p className="m-0 rounded-2xl bg-white p-6 text-slate-800 shadow-lg">Saving your progress...</p>
          </div>
        )}
      </ChessPuzzle.Root>
    );
  }

  return (
    <PuzzleWorkspace title="Woodpecker training" backTo="/woodpecker" backLabel="Back to training sets">
      <div className="mx-auto max-w-lg px-4 py-8 text-center">
        <p role={error ? 'alert' : 'status'}>{error || (puzzles.length ? 'All puzzles in this cycle are complete.' : 'Loading puzzles...')}</p>
        {puzzles.length > 0 && !error && (
          <Button variant="brand" size="action" disabled={isSaving} onClick={handleDone}>
            {isSaving ? 'Saving...' : 'Finish cycle'}
          </Button>
        )}
        {saveError && <p role="alert" className="mt-4 text-red-700">{saveError}</p>}
      </div>
    </PuzzleWorkspace>
  );
}
export default PuzzlePage;
